import { GlobalProductNames } from "@constants/products";
import { AnalysisVariable } from "@models/platform-analysis/enums/variableName";
import { BalanceAvailabilitiesParams } from "./utils";

export class FeeAnalysisSpecialProductNames {
  static readonly CFS_Edge = GlobalProductNames.CFSEdge;
  static readonly Expand = GlobalProductNames.Expand;
  static readonly North = GlobalProductNames.North;
  static readonly CPS = GlobalProductNames.CPS;

  static readonly VariableMap = new Map<string, AnalysisVariable[]>()
    .set(this.CFS_Edge, [AnalysisVariable.Edge_eligible_MF_balance])
    .set(this.North, [AnalysisVariable.NorthSelect_MF_balance])
    .set(this.CPS, [AnalysisVariable.CPSCore_MF_balance]);

  static readonly AdditionalVariableProductMap = new Map<
    string,
    AnalysisVariable[]
  >()
    .set(this.CFS_Edge, [
      AnalysisVariable.NorthSelect_MF_balance,
      AnalysisVariable.CPSCore_MF_balance,
    ])
    .set(this.North, [
      AnalysisVariable.Edge_eligible_MF_balance,
      AnalysisVariable.CPSCore_MF_balance,
    ])
    .set(this.CPS, [
      AnalysisVariable.Edge_eligible_MF_balance,
      AnalysisVariable.NorthSelect_MF_balance,
    ]);

  static readonly ProductsToHideSetupNames: string[] = [
    this.CFS_Edge,
    this.North,
    this.CPS,
  ];
}

export class FeeAnalysisBalanceVariableNames {
  static readonly IDPS_Cash_balance = AnalysisVariable.IDPS_Cash_balance;
  static readonly Super_Cash_balance = AnalysisVariable.Super_Cash_balance;
  static readonly MF_balance_i = AnalysisVariable.MF_balance_i;
  static readonly Edge_eligible_MF_balance =
    AnalysisVariable.Edge_eligible_MF_balance;
  static readonly NorthSelect_MF_balance =
    AnalysisVariable.NorthSelect_MF_balance;
  static readonly CPSCore_MF_balance = AnalysisVariable.CPSCore_MF_balance;
  static readonly TD_balance_i = AnalysisVariable.TD_balance_i;
  static readonly Intl_balance_i = AnalysisVariable.Intl_balance_i;
  static readonly AU_balance_i = AnalysisVariable.AU_balance_i;
  static readonly Bond_balance_i = AnalysisVariable.Bond_balance_i;

  static readonly CashBalances = [
    this.IDPS_Cash_balance,
    this.Super_Cash_balance,
  ];
  static readonly ManagedFundsBalances = [
    this.MF_balance_i,
    this.Edge_eligible_MF_balance,
    this.NorthSelect_MF_balance,
    this.CPSCore_MF_balance,
  ];
  static readonly TermDepositsBalances = [this.TD_balance_i];
  static readonly InternationalBalances = [this.Intl_balance_i];
  static readonly AustralianSharesBalances = [this.AU_balance_i];
  static readonly BondBalances = [this.Bond_balance_i];

  static readonly BalanceAvailabilities = [
    ...this.CashBalances,
    ...this.TermDepositsBalances,
    ...this.ManagedFundsBalances,
    ...this.InternationalBalances,
    ...this.AustralianSharesBalances,
    ...this.BondBalances,
  ];
}

export const VariableNameToBalanceMap: Partial<
  Record<
    AnalysisVariable,
    {
      availableIDPSKey: keyof BalanceAvailabilitiesParams;
      availableSuperKey: keyof BalanceAvailabilitiesParams;
    }
  >
> = {
  [AnalysisVariable.TD_balance_i]: {
    availableIDPSKey: "termDepositsAvailableInIDPS",
    availableSuperKey: "termDepositsAvailableInSuper",
  },
  [AnalysisVariable.MF_balance_i]: {
    availableIDPSKey: "unlistedManagedFundsAvailableInIDPS",
    availableSuperKey: "unlistedManagedFundsAvailableInSuper",
  },
  [AnalysisVariable.Edge_eligible_MF_balance]: {
    availableIDPSKey: "unlistedManagedFundsAvailableInIDPS",
    availableSuperKey: "unlistedManagedFundsAvailableInSuper",
  },
  [AnalysisVariable.NorthSelect_MF_balance]: {
    availableIDPSKey: "unlistedManagedFundsAvailableInIDPS",
    availableSuperKey: "unlistedManagedFundsAvailableInSuper",
  },
  [AnalysisVariable.Intl_balance_i]: {
    availableIDPSKey: "intlListedSecuritiesAvailableInIDPS",
    availableSuperKey: "intlListedSecuritiesAvailableInSuper",
  },
  [AnalysisVariable.AU_balance_i]: {
    availableIDPSKey: "auListedSecuritiesAvailableInIDPS",
    availableSuperKey: "auListedSecuritiesAvailableInSuper",
  },
  [AnalysisVariable.Bond_balance_i]: {
    availableIDPSKey: "fixedIncomeAvailableInIDPS",
    availableSuperKey: "fixedIncomeAvailableInSuper",
  },
  [AnalysisVariable.Super_Cash_balance]: {
    availableIDPSKey: "cashAvailableInIDPS",
    availableSuperKey: "cashAvailableInSuper",
  },
};
